import styled from "styled-components";

import { Collapse, MenuItem, Select, TextField } from "@mui/material";
import React from "react";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";

import { DataFormControl } from "./DataFormControl";

import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DropResult,
} from "react-beautiful-dnd";
import { StyledFormControlForSwitch, StyledSwitch } from "../../CommonUI/PropertyEditing/PropertyEditorStyles";
import { PropertyEditorColor } from "../../CommonUI/PropertyEditing/PropertyEditorColor";
import { PropertyEditorBoolean } from "../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { GridColDef } from "@mui/x-data-grid";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";

export class ColumnDefData {
    hide?: boolean;
    customHeader?: boolean;
    fontSize?: number;
    fontColor?: string;
}

export type ColumnDef = GridColDef & ColumnDefData;

const reorder = (list: ColumnDef[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export abstract class DataGridFormControlBase extends DataFormControl {
    columnValues: ColumnDef[] = [];
    selectedColumnSet?: number;
    dataGridMode: string = "Display";
    selectedValueName?: string;
    fontSize?: number;
    fontColor?: string;
    isEditing?: number;

    getDataDefinition = () => {
        const formContext = this.getFormContext();
        if (formContext) {
            const dataDefinition = formContext.form.inputData;
            if (dataDefinition) {
                return typeof dataDefinition === "function" ? dataDefinition() : dataDefinition;
            }
        }
        return null;
    };

    getDataSetFields = () => {
        const dataDefinition = this.getDataDefinition();
        const fields = dataDefinition?.fields;
        const columnSet: IDataDefinition[] = [];
        if (fields) {
            for (let field of fields) {
                if (field.list === true && field.type === "object") {
                    columnSet.push(field);
                }
            }
        }

        return columnSet;
    };

    getDataColumns = () => {
        const columnDefs: { field: string; type: string }[] = [];
        if (typeof this.selectedColumnSet === "undefined") {
            return [];
        }

        const fieldSet = this.getDataSetFields()[this.selectedColumnSet];

        if (fieldSet && fieldSet.fields) {
            for (let field of fieldSet.fields) {
                if (field.list === false && field.type !== "object" && field.type !== "file")
                    columnDefs.push({
                        field: field.name,
                        type: field.type as string,
                    });
            }
        }

        return columnDefs;
    };

    onColumnDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        this.columnValues = reorder(this.columnValues, result.source.index, result.destination.index);
        StateManager.propertyChanged(this, "setColumnValue");
    };

    onSelectColumnSet = (e: any) => {
        const value = e.target.value;
        this.selectedColumnSet = parseInt(value);

        this.columnValues = this.getDataColumns().map((columnItem) => ({
            field: columnItem.field,
            headerName: columnItem.field,
            hide: false,
            isEditing: false,
            sortable: false,
            filterable: false,
            customHeader: false,
        }));

        StateManager.propertyChanged(this, "setColumnValue");
    };

    changeColumnConfig = (value: any, columnIndex: number, configField: string) => {
        this.columnValues[columnIndex] = {
            ...this.columnValues[columnIndex],
            [configField]: value,
        };

        StateManager.propertyChanged(this, "setColumnValue");
    };

    toggleCollapse = (e: any, index: number) => {
        if (this.isEditing === index) {
            this.isEditing = -1;
        } else {
            this.isEditing = index;
        }
        StateManager.propertyChanged(this, "setColumnValue");
    };

    changeWidth = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, columnInex: number) => {
        const { value } = event.target;
        this.columnValues[columnInex].width = parseInt(value);

        StateManager.propertyChanged(this, "setColumnValue");
    };

    // produces part of the editor for label and name
    renderDataGridColumnControlEditor() {
        return (
            <ObserveState
                listenTo={this}
                properties={["setColumnValue"]}
                control={() => (
                    <UISpacedBoxContent>
                        <Select
                            value={typeof this.selectedColumnSet === "undefined" ? -1 : this.selectedColumnSet}
                            label="Column Set"
                            placeholder="Select Column Set"
                            onChange={this.onSelectColumnSet}
                            variant="outlined"
                        >
                            {this.getDataSetFields().map((columnSet, index) => (
                                <MenuItem
                                    key={columnSet.name}
                                    value={index}
                                >
                                    {columnSet.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {typeof this.selectedColumnSet !== "undefined" &&
                            this.getDataSetFields()[this.selectedColumnSet] != null && (
                                <>
                                    <DragDropContext onDragEnd={this.onColumnDragEnd}>
                                        <Droppable droppableId="columnValueDrop">
                                            {(provided) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    {this.columnValues.length > 0 &&
                                                        this.columnValues.map(
                                                            (columnValue: ColumnDef, index: number) => (
                                                                <Draggable
                                                                    key={columnValue.field}
                                                                    draggableId={columnValue.field!}
                                                                    index={index}
                                                                >
                                                                    {(
                                                                        provided: DraggableProvided,
                                                                        snapshot: DraggableStateSnapshot,
                                                                    ) => {
                                                                        return (
                                                                            <>
                                                                                <DragItemDiv
                                                                                    ref={(node: HTMLDivElement) => {
                                                                                        provided.innerRef(node);
                                                                                    }}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    style={{
                                                                                        ...(provided.draggableProps
                                                                                            .style || {}),
                                                                                        cursor: snapshot.isDragging
                                                                                            ? "move"
                                                                                            : "default",
                                                                                    }}
                                                                                    onClick={(e) =>
                                                                                        this.toggleCollapse(e, index)
                                                                                    }
                                                                                >
                                                                                    {columnValue.field}
                                                                                </DragItemDiv>
                                                                                <StyledCollapse
                                                                                    in={this.isEditing === index}
                                                                                >
                                                                                    <TextField
                                                                                        variant="outlined"
                                                                                        label="Header Name"
                                                                                        onChange={(e) =>
                                                                                            this.changeColumnConfig(
                                                                                                e.target.value,
                                                                                                index,
                                                                                                "headerName",
                                                                                            )
                                                                                        }
                                                                                        value={columnValue.headerName}
                                                                                    />
                                                                                    <TextField
                                                                                        type="number"
                                                                                        variant="outlined"
                                                                                        label="Width"
                                                                                        onChange={(e) =>
                                                                                            this.changeColumnConfig(
                                                                                                e.target.value,
                                                                                                index,
                                                                                                "width",
                                                                                            )
                                                                                        }
                                                                                        value={columnValue.width}
                                                                                    />
                                                                                    <StyledFormControlForSwitch
                                                                                        control={
                                                                                            <StyledSwitch
                                                                                                name="Sortable"
                                                                                                sx={{
                                                                                                    color: UIStyleConstants.BLUE_COLOR,
                                                                                                }}
                                                                                                checked={
                                                                                                    columnValue.sortable ||
                                                                                                    false
                                                                                                }
                                                                                                onChange={(e) =>
                                                                                                    this.changeColumnConfig(
                                                                                                        e.target
                                                                                                            .checked,
                                                                                                        index,
                                                                                                        "sortable",
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                        label="Sortable"
                                                                                    />
                                                                                    <StyledFormControlForSwitch
                                                                                        control={
                                                                                            <StyledSwitch
                                                                                                name="Filterable"
                                                                                                sx={{
                                                                                                    color: UIStyleConstants.BLUE_COLOR,
                                                                                                }}
                                                                                                checked={
                                                                                                    columnValue.filterable ||
                                                                                                    false
                                                                                                }
                                                                                                onChange={(e) =>
                                                                                                    this.changeColumnConfig(
                                                                                                        e.target
                                                                                                            .checked,
                                                                                                        index,
                                                                                                        "filterable",
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                        label="Filterable"
                                                                                    />
                                                                                    <StyledFormControlForSwitch
                                                                                        control={
                                                                                            <StyledSwitch
                                                                                                name="Include"
                                                                                                sx={{
                                                                                                    color: UIStyleConstants.BLUE_COLOR,
                                                                                                }}
                                                                                                checked={
                                                                                                    !columnValue.hide
                                                                                                }
                                                                                                onChange={(e) =>
                                                                                                    this.changeColumnConfig(
                                                                                                        !e.target
                                                                                                            .checked,
                                                                                                        index,
                                                                                                        "hide",
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                        label="Include"
                                                                                    />
                                                                                    <PropertyEditorBoolean
                                                                                        editObject={columnValue}
                                                                                        label={
                                                                                            "Change header color and font"
                                                                                        }
                                                                                        propertyName={"customHeader"}
                                                                                    />
                                                                                    <PropertyEditorCollapse
                                                                                        editObject={columnValue}
                                                                                        collapseLogicOrField={
                                                                                            "customHeader"
                                                                                        }
                                                                                    >
                                                                                        <TextField
                                                                                            type="number"
                                                                                            variant="outlined"
                                                                                            label="Font Size"
                                                                                            onChange={(e) =>
                                                                                                this.changeColumnConfig(
                                                                                                    e.target.value,
                                                                                                    index,
                                                                                                    "fontSize",
                                                                                                )
                                                                                            }
                                                                                            value={columnValue.fontSize}
                                                                                        />
                                                                                        <PropertyEditorColor
                                                                                            label="Font Color"
                                                                                            editObject={columnValue}
                                                                                            propertyName="fontColor"
                                                                                            name="fontColor"
                                                                                        />
                                                                                    </PropertyEditorCollapse>
                                                                                </StyledCollapse>
                                                                            </>
                                                                        );
                                                                    }}
                                                                </Draggable>
                                                            ),
                                                        )}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </>
                            )}
                    </UISpacedBoxContent>
                )}
            />
        );
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        DataUtilities.addStorageData(
            this,
            retData,
            "columnValues",
            "selectedColumnSet",
            "dataGridMode",
            "selectedValueName",
            "fontSize",
            "fontColor",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "columnValues",
            "selectedColumnSet",
            "dataGridMode",
            "selectedValueName",
            "fontSize",
            "fontColor",
        );
    }
}

const DragItemDiv = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 2px solid;
    border-radius: 4px;
    margin-bottom: 2px;
    border-color: ${UIStyleConstants.BLUE_COLOR};
    padding: 8px 10px;
`;

const StyledCollapse = styled(Collapse)`
    & > .MuiCollapse-wrapper {
        padding: 12px 4px;
        border: 2px solid ${UIStyleConstants.BLUE_COLOR};
        border-radius: 4px;
        margin-bottom: 10px;
    }
`;
